import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { toast } from "sonner";
import { Check, Undo2, X } from "lucide-react";

import { useEstimates } from "@/braining/store";
import { counted } from "@/braining/format";
import { loadLearning, POLICY_ORDER, type LearningState } from "@/braining/learning.functions";
import { publishBadge } from "@/braining/badge-count";
import {
  applySuggestion,
  decide,
  revertEntry,
  suggestionReason,
  suggestionTitle,
  AUTO_MIN_OBS,
  SUGGEST_MIN_OBS,
  type LearningLogEntry,
  type LearningMode,
  type Suggestion,
  type SuggestionKind,
  revertFor,
} from "@/braining/library-suggestions";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export const Route = createFileRoute("/learning")({
  // Правки одного менеджера — его привычки, а не правило агентства: под RLS он
  // видит только свои сметы, и выводить из них эталон нельзя. Поэтому раздел
  // закрыт не только в панели, но и по адресу.
  beforeLoad: ({ context }) => {
    const role = context.user?.role;
    if (role !== "admin" && role !== "rop") throw redirect({ to: "/" });
  },
  component: LearningPage,
});

/**
 * Чему справочник научился у менеджеров.
 *
 * Своим разделом, а не вкладкой в справочнике, по двум причинам. Справочник
 * открывают раз в месяц — вкладку в нём никто бы не открыл, а вся ценность
 * петли в том, что кто-то на неё смотрит; счётчик в панели для того и нужен.
 * И половина предложений вообще не про блоки, а про прайс — под «Блоками
 * работ» им было тесно.
 */
function LearningPage() {
  const { library, ratecard, updateLibrary, updateRatecard, adoptLibrary } = useEstimates();
  const [state, setState] = useState<LearningState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(() => {
    loadLearning()
      .then((s) => {
        setState(s);
        publishBadge("learning", s.suggestions.length);
        // Автоприменение только что поменяло справочник на сервере — берём
        // его версию, иначе следующая правка уйдёт со старой отметкой.
        if (s.library) adoptLibrary(s.library);
      })
      .catch((e: unknown) => {
        // Ошибка сети или сервера не должна выглядеть как честное «предложений нет».
        console.error("[learning] не удалось загрузить", e);
        toast.error("Не удалось загрузить предложения — попробуйте обновить страницу");
        setState({ suggestions: [], log: [], policy: DEFAULTS, appliedNow: [] });
      });
  }, [adoptLibrary]);

  useEffect(reload, [reload]);

  async function accept(s: Suggestion) {
    setBusy(s.key);
    try {
      const next = applySuggestion(s, library, ratecard);
      await updateLibrary(
        decide(next.library, s, "accepted", {
          title: suggestionTitle(s),
          revert: revertFor(s, { library }, next),
        }),
      );
      if (next.ratecard !== ratecard) await updateRatecard(next.ratecard);
      setState((p) => {
        const next = p && { ...p, suggestions: p.suggestions.filter((x) => x.key !== s.key) };
        if (next) publishBadge("learning", next.suggestions.length);
        return next;
      });
      toast.success("Справочник пополнен");
      reload();
    } catch {
      toast.error("Не удалось применить предложение");
    } finally {
      setBusy(null);
    }
  }

  async function dismiss(s: Suggestion) {
    setBusy(s.key);
    try {
      await updateLibrary(decide(library, s, "dismissed", { title: suggestionTitle(s) }));
      setState((p) => {
        const next = p && { ...p, suggestions: p.suggestions.filter((x) => x.key !== s.key) };
        if (next) publishBadge("learning", next.suggestions.length);
        return next;
      });
      reload();
    } catch {
      toast.error("Не удалось отклонить предложение");
    } finally {
      setBusy(null);
    }
  }

  async function revert(entry: LearningLogEntry) {
    setBusy(entry.key);
    try {
      const next = revertEntry(entry, library, ratecard);
      await updateLibrary(next.library);
      if (next.ratecard !== ratecard) await updateRatecard(next.ratecard);
      toast.success("Вернул как было");
      reload();
    } catch {
      toast.error("Не удалось откатить");
    } finally {
      setBusy(null);
    }
  }

  async function setMode(kind: SuggestionKind, mode: LearningMode) {
    await updateLibrary({
      ...library,
      learningPolicy: { ...(library.learningPolicy ?? {}), [kind]: mode },
    }).catch(() => toast.error("Не удалось сохранить режим"));
    setState((p) => p && { ...p, policy: { ...p.policy, [kind]: mode } });
    reload();
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Обучение</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Система сравнивает, что предложила, с тем, что ушло клиенту, и предлагает пополнить
          справочник. В счёт идут выпущенные сметы — скачанные клиенту или проверенные
          руководителем: ждать формального согласования значило бы учиться на дисциплине, а не на
          работе.
        </p>
      </div>

      {state?.appliedNow.length ? (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="space-y-1 py-3 text-sm">
            <p className="font-medium">
              Пока вас не было, система применила{" "}
              {counted(state.appliedNow.length, "предложение", "предложения", "предложений")}:
            </p>
            <ul className="list-disc pl-4 text-muted-foreground">
              {state.appliedNow.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              Это виды, помеченные ниже как «сам». Любое можно откатить в журнале.
            </p>
          </CardContent>
        </Card>
      ) : null}

      <Section title="Предложения">
        {state === null ? (
          <p className="text-sm text-muted-foreground">Смотрим выпущенные калькуляторы…</p>
        ) : state.suggestions.length === 0 ? (
          <p className="max-w-2xl text-sm text-muted-foreground">
            Пока ничего. Предложение появляется, когда одну и ту же правку повторяют хотя бы в{" "}
            {SUGGEST_MIN_OBS} выпущенных калькуляторах.
          </p>
        ) : (
          <div className="grid gap-3">
            {state.suggestions.map((s) => (
              <SuggestionCard
                key={s.key}
                suggestion={s}
                busy={busy === s.key}
                onAccept={() => void accept(s)}
                onDismiss={() => void dismiss(s)}
              />
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Что система вправе делать сама"
        note={`Само применяется то, что не двигает деньги в собранных сметах. Для этого нужно ${AUTO_MIN_OBS} наблюдения, а не ${SUGGEST_MIN_OBS}: сделать и ошибиться дороже, чем спросить и ошибиться.`}
      >
        <Card>
          <CardContent className="divide-y py-1">
            {(Object.keys(POLICY_ORDER) as SuggestionKind[]).map((kind) => (
              <div key={kind} className="flex items-center justify-between gap-4 py-2.5">
                <span className="text-sm">{POLICY_ORDER[kind]}</span>
                <div className="flex shrink-0 gap-1">
                  {(["auto", "ask"] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => void setMode(kind, mode)}
                      className={cn(
                        "rounded-full border px-3 py-1 text-xs transition-colors",
                        (state?.policy[kind] ?? DEFAULTS[kind]) === mode
                          ? "border-primary bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:border-input hover:text-foreground",
                      )}
                    >
                      {mode === "auto" ? "сам" : "спросить"}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </Section>

      <Section title="Журнал" note="Что приняли, что отклонили и что система сделала сама.">
        {!state?.log.length ? (
          <p className="text-sm text-muted-foreground">Пока пусто.</p>
        ) : (
          <Card>
            <CardContent className="divide-y py-1">
              {state.log.map((e) => (
                <div
                  key={e.key + e.at}
                  className="flex items-baseline justify-between gap-4 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm">{e.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {ACTION_LABEL[e.action]}
                      {e.by ? ` · ${e.by}` : ""} · {new Date(e.at).toLocaleDateString("ru-RU")} ·{" "}
                      {/* У выученного из смет наблюдение — калькулятор в
                          системе, у найденного при обходе папки — встреча в
                          чужой книге. Называть второе калькулятором значит
                          обещать ссылку, которой нет. */}
                      {e.kind === "corpus_block"
                        ? `${counted(e.n, "встреча", "встречи", "встреч")} в архиве`
                        : counted(e.n, "калькулятор", "калькулятора", "калькуляторов")}
                    </p>
                  </div>
                  {/* Откат — только у того, что система применила сама: там
                      человек ничего не решал. Принятое руками отменяется так
                      же, как любая другая правка справочника, — руками. */}
                  {e.revert && e.action === "auto" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === e.key}
                      onClick={() => void revert(e)}
                    >
                      <Undo2 className="size-4" />
                      Откатить
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </Section>
    </div>
  );
}

const ACTION_LABEL: Record<LearningLogEntry["action"], string> = {
  auto: "система применила сама",
  accepted: "принято",
  dismissed: "отклонено",
};

/** Умолчание для первого рендера, пока сервер не ответил. */
const DEFAULTS: Record<SuggestionKind, LearningMode> = {
  ratecard_role: "auto",
  new_block: "auto",
  block_line_added: "ask",
  block_line_removed: "ask",
  block_hours: "ask",
  ratecard_rate: "ask",
  corpus_block: "ask",
};

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div>
        <h2 className="text-sm font-medium">{title}</h2>
        {note && <p className="mt-0.5 max-w-3xl text-xs text-muted-foreground">{note}</p>}
      </div>
      {children}
    </div>
  );
}

/**
 * Карточка несёт то, из чего выросла.
 *
 * Кеша описывал это так: «система должна говорить — у тебя тут новое, я
 * правильно понимаю, что надо пополнить справочник вот таким-то?». Вопрос без
 * оснований — это гадание, поэтому рядом всегда видно, сколько раз повторилось
 * и в каких калькуляторах; на них можно уйти и посмотреть самому.
 */
function SuggestionCard({
  suggestion: s,
  busy,
  onAccept,
  onDismiss,
}: {
  suggestion: Suggestion;
  busy: boolean;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const { estimates } = useEstimates();

  return (
    <Card>
      <CardContent className="flex flex-wrap items-start justify-between gap-4 py-4">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium">{suggestionTitle(s)}</p>
          <p className="text-sm text-muted-foreground">{suggestionReason(s)}</p>
          {/*
            Ссылки — только туда, куда можно дойти. Предложение из обхода
            папки растёт из книг агентства, которых в системе нет: строка
            «5 калькуляторов:» с пустым списком после неё выглядела бы
            потерянными ссылками, хотя терять нечего.
          */}
          {s.estimateIds.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {counted(s.n, "калькулятор", "калькулятора", "калькуляторов")}:{" "}
              {s.estimateIds.map((id, i) => (
                <span key={id}>
                  {i > 0 && ", "}
                  <Link to="/estimates/$id" params={{ id }} className="underline">
                    {estimates.find((e) => e.id === id)?.title || "без названия"}
                  </Link>
                </span>
              ))}
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" disabled={busy} onClick={onAccept}>
            <Check className="size-4" />
            Принять
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={onDismiss}>
            <X className="size-4" />
            Отклонить
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
